import { useEffect, useState, useCallback, useMemo } from 'react';
import { useGameStateStore } from '@shared/stores';

type MenuOption = 'Start Game' | 'Server List' | 'Create Lobby' | 'Settings' | 'Admin';

const BASE_MENU_OPTIONS: MenuOption[] = ['Start Game', 'Server List', 'Create Lobby', 'Settings'];

/**
 * Check if dev menu is enabled via environment variable.
 */
function isDevMenuEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_DEV_MENU === 'true';
}

export default function MainMenu() {
  const { startGame, setScreen } = useGameStateStore();
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Include Admin option only when env variable is enabled
  const menuOptions = useMemo(() => {
    if (isDevMenuEnabled()) {
      return [...BASE_MENU_OPTIONS, 'Admin'];
    }
    return BASE_MENU_OPTIONS;
  }, []);

  const handleMenuAction = useCallback(
    (option: MenuOption) => {
      switch (option) {
        case 'Start Game':
          startGame();
          break;
        case 'Settings':
          setScreen('settings');
          break;
        case 'Admin':
          setScreen('devMenu');
          break;
        case 'Server List':
        case 'Create Lobby':
          // No-op for now, placeholder for future functionality
          break;
        default:
          break;
      }
    },
    [startGame, setScreen]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) => (prev === 0 ? menuOptions.length - 1 : prev - 1));
          break;
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) => (prev === menuOptions.length - 1 ? 0 : prev + 1));
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          handleMenuAction(menuOptions[selectedIndex] as MenuOption);
          break;
        default:
          break;
      }
    },
    [selectedIndex, handleMenuAction, menuOptions]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div
      className="w-screen h-screen relative bg-cover bg-center overflow-hidden"
      style={{ backgroundImage: "url('/assets/backgrounds/main_menu/menu_background.webp')" }}
    >
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-black/60" />

      <div className="absolute inset-0 flex flex-col items-center justify-center gap-12">
        <h1 className="text-4xl font-bold text-white drop-shadow-lg">Castle Survivors</h1>
        <div className="flex flex-col gap-4">
          {menuOptions.map((option, index) => (
            <button
              key={option}
              onClick={() => handleMenuAction(option as MenuOption)}
              className={`px-8 py-3 text-lg font-semibold rounded transition-colors ${
                index === selectedIndex
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
